import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Save, CheckCircle, MessageSquare, 
  Paperclip, Download, Building, Users, Calendar,
  Award, AlertCircle, LogOut, X, Star, Lightbulb,
  ChevronRight, Eye, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { PILLAR_NAMES } from '../data/pillars';

// C-N-E-S-S Level labels
const CNESS_LEVELS = {
  1: { label: 'C - Starting the Journey', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: '🌱' },
  2: { label: 'N - Building Foundation', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '🏗️' },
  3: { label: 'E - Growing Strong', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: '📈' },
  4: { label: 'S - Leading the Way', color: 'bg-indigo-100 text-indigo-800 border-indigo-300', icon: '🚀' },
  5: { label: 'S - Setting the Standard', color: 'bg-green-100 text-green-800 border-green-300', icon: '🏆' }
};


// Scoring rubric (0-5 scale)
const SCORING_LEVELS = [
  { score: 5, label: 'Exemplary', points: 20, color: 'bg-green-500', description: 'Exceeds all expectations' },
  { score: 4, label: 'Advanced', points: 16, color: 'bg-blue-500', description: 'Strong performance' },
  { score: 3, label: 'Established', points: 12, color: 'bg-teal-500', description: 'Meets expectations' },
  { score: 2, label: 'Developing', points: 8, color: 'bg-yellow-500', description: 'Partial implementation' },
  { score: 1, label: 'Beginning', points: 4, color: 'bg-orange-500', description: 'Initial stages' },
  { score: 0, label: 'Not Addressed', points: 0, color: 'bg-red-500', description: 'No evidence' },
];

const EvaluatorAssessmentPage = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState(null);
  const [assignment, setAssignment] = useState(null);
  const [orgResponses, setOrgResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [scores, setScores] = useState({});
  const [currentPillar, setCurrentPillar] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('qualitative');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [availablePillars, setAvailablePillars] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const evaluatorId = localStorage.getItem('user_id');

  // Auth check and data fetch
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('cness_authenticated') === 'true';
    const userRole = localStorage.getItem('cness_user_role');
    
    if (!isAuthenticated || userRole !== 'evaluator') {
      navigate('/login');
      return;
    }
    
    fetchData();
    updateOnlineStatus(true);
    
    const heartbeat = setInterval(() => updateOnlineStatus(true), 30000);
    return () => {
      clearInterval(heartbeat);
      updateOnlineStatus(false);
    };
  }, [organizationId]);

  const updateOnlineStatus = async (online) => {
    try {
      await fetch(`${BACKEND_URL}/api/evaluator/online-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          evaluator_id: evaluatorId,
          organization_id: organizationId,
          is_online: online
        })
      });
    } catch (e) {
      console.log('Failed to update online status');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization details
      const orgResponse = await fetch(`${BACKEND_URL}/api/organizations/${organizationId}`);
      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        setOrganization(orgData);
      }

      // Fetch assignment details
      const assignResponse = await fetch(`${BACKEND_URL}/api/evaluator-assignments/${evaluatorId}`);
      if (assignResponse.ok) {
        const assignData = await assignResponse.json();
        const assignments = assignData.assignments || assignData || [];
        const thisAssignment = assignments.find(a => a.organization_id === organizationId);
        setAssignment(thisAssignment);
      }

      // Fetch organization responses FIRST - this determines what we show
      const responsesResponse = await fetch(`${BACKEND_URL}/api/organization-responses/${organizationId}`);
      if (responsesResponse.ok) {
        const responsesData = await responsesResponse.json();
        const responsesList = responsesData.responses || responsesData || [];
        
        // Build response map by question_id
        const responsesMap = {};
        responsesList.forEach(r => { 
          responsesMap[r.question_id] = r; 
        });
        setOrgResponses(responsesMap);
        
        // Determine which pillars have responses
        const pillarsWithResponses = [...new Set(responsesList.map(r => r.pillar_number))].sort((a, b) => a - b);
        setAvailablePillars(pillarsWithResponses);
        
        // Set initial pillar to first pillar with responses
        if (pillarsWithResponses.length > 0 && !currentPillar) {
          setCurrentPillar(pillarsWithResponses[0]);
        }
        
        console.log('Pillars with responses:', pillarsWithResponses);
        console.log('Total org responses:', responsesList.length);
      }

      // Fetch questions
      const questionsResponse = await fetch(`${BACKEND_URL}/api/assessments/questions`);
      const questionsData = await questionsResponse.json();
      const allQuestions = Array.isArray(questionsData) ? questionsData.filter(q => q.is_active) : [];
      setQuestions(allQuestions);

      // Fetch existing evaluator scores
      try {
        const scoresResponse = await fetch(`${BACKEND_URL}/api/evaluator-scores/${organizationId}/${evaluatorId}`);
        if (scoresResponse.ok) {
          const scoresData = await scoresResponse.json();
          const scoresList = scoresData.scores || scoresData || [];
          const scoresMap = {};
          // Mark all fetched scores as saved (they're already in database)
          scoresList.forEach(s => { 
            scoresMap[s.question_id] = { ...s, saved: true }; 
          });
          setScores(scoresMap);
        }
      } catch (e) {
        console.log('No existing scores');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading assessment data');
    } finally {
      setLoading(false);
    }
  };

  // Get questions for current pillar that the org has answered
  const getFilteredQuestions = useCallback(() => {
    if (!currentPillar) return [];
    
    // Get question IDs that the org has responded to for this pillar
    const answeredQuestionIds = Object.keys(orgResponses).filter(qId => {
      const response = orgResponses[qId];
      return response.pillar_number === currentPillar;
    });
    
    // Get the full question objects for answered questions
    let pillarQs = questions.filter(q => answeredQuestionIds.includes(q.question_id));
    
    // Sort by question number
    pillarQs.sort((a, b) => {
      const aNum = parseInt(a.question_id?.match(/Q(\d+)/)?.[1] || '0');
      const bNum = parseInt(b.question_id?.match(/Q(\d+)/)?.[1] || '0');
      return aNum - bNum;
    });
    
    // Filter by tab (qualitative vs quantitative)
    if (activeTab === 'qualitative') {
      return pillarQs.filter(q => {
        const qNum = parseInt(q.question_id?.match(/Q(\d+)/)?.[1] || '0');
        return q.question_type === 'qualitative' || qNum <= 6;
      });
    } else {
      return pillarQs.filter(q => {
        const qNum = parseInt(q.question_id?.match(/Q(\d+)/)?.[1] || '0');
        return q.question_type === 'quantitative' || qNum > 6;
      });
    }
  }, [currentPillar, orgResponses, questions, activeTab]);

  const filteredQuestions = getFilteredQuestions();
  const currentQuestion = filteredQuestions[currentQuestionIndex];
  const currentOrgResponse = currentQuestion ? orgResponses[currentQuestion.question_id] : null;

  const handleScoreSelect = (level) => {
    if (!currentQuestion) return;
    setHasUnsavedChanges(true);
    
    setScores(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        question_id: currentQuestion.question_id,
        score: level.score,
        points: level.points,
        level_label: level.label
      }
    }));
  };

  const handleRemarksChange = (value) => {
    if (!currentQuestion) return;
    setHasUnsavedChanges(true);
    
    setScores(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        evaluator_remarks: value
      }
    }));
  };

  const handleSaveScore = async (exitAfter = false) => {
    if (!currentQuestion) return;

    const questionScore = scores[currentQuestion.question_id];
    if (questionScore?.score === undefined) {
      toast.error('Please select a score');
      return;
    }

    if (!questionScore.evaluator_remarks?.trim()) {
      toast.error('Remarks are required');
      return;
    }

    setSaving(true);
    try {
      const scoreData = {
        organization_id: organizationId,
        evaluator_id: evaluatorId,
        question_id: currentQuestion.question_id,
        pillar_number: currentQuestion.pillar_number || currentPillar,
        pillar_name: PILLAR_NAMES[currentQuestion.pillar_number || currentPillar],
        score: questionScore.score,
        points: questionScore.points,
        selected_level: questionScore.score.toString(),
        level_label: questionScore.level_label,
        evaluator_remarks: questionScore.evaluator_remarks,
        question_text: currentQuestion.question_text,
        question_type: activeTab
      };

      const response = await fetch(`${BACKEND_URL}/api/evaluator-scoring/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(scoreData)
      });

      if (response.ok) {
        // Mark this score as saved
        setScores(prev => ({
          ...prev,
          [currentQuestion.question_id]: {
            ...prev[currentQuestion.question_id],
            saved: true
          }
        }));
        setHasUnsavedChanges(false);
        toast.success('Score saved');
        
        if (exitAfter) {
          navigate('/evaluator/dashboard');
          return;
        }

        // Move to next question
        if (currentQuestionIndex < filteredQuestions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          // Try next tab or pillar
          if (activeTab === 'qualitative') {
            setActiveTab('quantitative');
            setCurrentQuestionIndex(0);
          } else {
            const currentPillarIndex = availablePillars.indexOf(currentPillar);
            if (currentPillarIndex < availablePillars.length - 1) {
              setCurrentPillar(availablePillars[currentPillarIndex + 1]);
              setActiveTab('qualitative');
              setCurrentQuestionIndex(0);
            }
          }
        }
      } else {
        toast.error('Error saving');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error saving');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteEvaluation = async () => {
    const totalOrgResponses = Object.keys(orgResponses).length;
    const scoredCount = Object.values(scores).filter(s => s.score !== undefined).length;
    
    if (scoredCount < totalOrgResponses) {
      toast.error(`Please score all ${totalOrgResponses} questions. Currently scored: ${scoredCount}`);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/evaluator-scoring/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: organizationId,
          evaluator_id: evaluatorId
        })
      });

      if (response.ok) {
        toast.success('Evaluation completed!');
        navigate('/evaluator/dashboard');
      } else {
        toast.error('Error completing');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error completing');
    } finally {
      setSaving(false);
    }
  };

  // Calculate progress - only count saved scores (scores with score defined AND evaluator_remarks saved)
  const totalOrgResponses = Object.keys(orgResponses).length;
  // Only count scores that were saved (have both score and remarks)
  const savedScores = Object.values(scores).filter(s => s.score !== undefined && s.saved === true);
  const scoredCount = savedScores.length;
  const progressPercent = totalOrgResponses > 0 ? Math.round((scoredCount / totalOrgResponses) * 100) : 0;

  // Get pillar progress - only saved scores
  const getPillarProgress = (pillarNum) => {
    const pillarResponses = Object.keys(orgResponses).filter(qId => orgResponses[qId].pillar_number === pillarNum);
    const scored = pillarResponses.filter(qId => scores[qId]?.saved === true).length;
    return { scored, total: pillarResponses.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading assessment...</p>
        </div>
      </div>
    );
  }

  const orgName = organization?.name || assignment?.organization_name || 'Organization';
  // Display organization size from database - format properly (uppercase)
  const rawOrgSize = organization?.organization_size || assignment?.organization_size || '';
  const orgSize = rawOrgSize ? rawOrgSize.toUpperCase() : 'N/A';
  const currentScore = scores[currentQuestion?.question_id];
  const orgLevelInfo = currentOrgResponse?.selected_level ? CNESS_LEVELS[currentOrgResponse.selected_level] : null;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Clean Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => hasUnsavedChanges ? setShowExitModal(true) : navigate('/evaluator/dashboard')} 
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{orgName}</h1>
              <p className="text-sm text-slate-400">{orgSize} • {totalOrgResponses} Questions to Evaluate</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-2xl font-bold text-cyan-400">{scoredCount}/{totalOrgResponses}</p>
              <p className="text-xs text-slate-400">{progressPercent}% Complete</p>
            </div>
            
            {scoredCount >= totalOrgResponses && totalOrgResponses > 0 && (
              <button
                onClick={handleCompleteEvaluation}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-600 rounded-lg font-semibold hover:bg-emerald-500 transition-colors"
              >
                <CheckCircle className="w-5 h-5" />
                Complete
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Sidebar - Only Pillars with Responses */}
        <aside className="w-64 bg-slate-800 border-r border-slate-700 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
              Organization's Pillars ({availablePillars.length})
            </h2>
            
            {availablePillars.length === 0 ? (
              <p className="text-slate-500 text-sm">No responses yet</p>
            ) : (
              <div className="space-y-2">
                {availablePillars.map(pillarNum => {
                  const progress = getPillarProgress(pillarNum);
                  const isComplete = progress.scored === progress.total;
                  const isCurrent = currentPillar === pillarNum;
                  
                  return (
                    <button
                      key={pillarNum}
                      onClick={() => { setCurrentPillar(pillarNum); setCurrentQuestionIndex(0); setActiveTab('qualitative'); }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        isCurrent ? 'bg-cyan-600 text-white' :
                        isComplete ? 'bg-emerald-900/50 text-emerald-400' :
                        'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs opacity-70">Pillar {pillarNum}</p>
                          <p className="font-medium text-sm">{PILLAR_NAMES[pillarNum]}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isComplete && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                          <span className="text-xs font-bold bg-slate-900/50 px-2 py-1 rounded">
                            {progress.scored}/{progress.total}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          {!currentPillar ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-slate-400 mb-2">No Questions to Evaluate</h2>
                <p className="text-slate-500">The organization hasn't submitted any responses yet.</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Tabs */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => { setActiveTab('qualitative'); setCurrentQuestionIndex(0); }}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    activeTab === 'qualitative' ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  Qualitative
                </button>
                <button
                  onClick={() => { setActiveTab('quantitative'); setCurrentQuestionIndex(0); }}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    activeTab === 'quantitative' ? 'bg-cyan-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                  }`}
                >
                  Quantitative
                </button>
              </div>

              {!currentQuestion ? (
                <div className="bg-slate-800 rounded-xl p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h2 className="text-xl font-bold mb-2">Section Complete</h2>
                  <p className="text-slate-400 mb-6">All {activeTab} questions for this pillar are scored.</p>
                  <button
                    onClick={() => {
                      if (activeTab === 'qualitative') {
                        setActiveTab('quantitative');
                        setCurrentQuestionIndex(0);
                      } else {
                        const idx = availablePillars.indexOf(currentPillar);
                        if (idx < availablePillars.length - 1) {
                          setCurrentPillar(availablePillars[idx + 1]);
                          setActiveTab('qualitative');
                          setCurrentQuestionIndex(0);
                        }
                      }
                    }}
                    className="px-6 py-2 bg-cyan-600 rounded-lg font-semibold hover:bg-cyan-500"
                  >
                    Continue
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Question Card */}
                  <div className="bg-slate-800 rounded-xl overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4">
                      <div className="flex items-center justify-between text-sm text-cyan-200 mb-2">
                        <span>Question {currentQuestionIndex + 1} of {filteredQuestions.length}</span>
                        <span>{PILLAR_NAMES[currentPillar]}</span>
                      </div>
                      <h2 className="text-lg font-bold">{currentQuestion.question_text}</h2>
                      <p className="text-xs text-cyan-200 mt-1">ID: {currentQuestion.question_id}</p>
                    </div>

                    {/* Organization's Response */}
                    <div className="p-6 bg-slate-700/50">
                      <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Organization's Answer
                      </h3>
                      
                      {currentOrgResponse ? (
                        <div className="space-y-4">
                          {/* Selected Level */}
                          {orgLevelInfo && (
                            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${orgLevelInfo.color}`}>
                              <span className="text-lg">{orgLevelInfo.icon}</span>
                              <span className="font-bold">{orgLevelInfo.label}</span>
                            </div>
                          )}

                          {/* Notes */}
                          {currentOrgResponse.additional_notes && (
                            <div className="bg-slate-800 rounded-lg p-4">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Notes</p>
                              <p className="text-slate-300">{currentOrgResponse.additional_notes}</p>
                            </div>
                          )}

                          {/* Best Practice */}
                          {currentOrgResponse.best_practice && (
                            <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4">
                              <p className="text-xs font-bold text-emerald-400 uppercase mb-2 flex items-center gap-2">
                                <Star className="w-4 h-4" />
                                Best Practice
                              </p>
                              <p className="text-emerald-200">{currentOrgResponse.best_practice}</p>
                            </div>
                          )}

                          {/* Improvement Idea */}
                          {currentOrgResponse.improvement_idea && (
                            <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4">
                              <p className="text-xs font-bold text-amber-400 uppercase mb-2 flex items-center gap-2">
                                <Lightbulb className="w-4 h-4" />
                                Improvement Goal
                              </p>
                              <p className="text-amber-200">{currentOrgResponse.improvement_idea}</p>
                            </div>
                          )}

                          {/* Attachments */}
                          {currentOrgResponse.attachments?.length > 0 && (
                            <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4">
                              <p className="text-xs font-bold text-purple-400 uppercase mb-2 flex items-center gap-2">
                                <Paperclip className="w-4 h-4" />
                                Attachments
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {currentOrgResponse.attachments.map((att, idx) => (
                                  <a key={idx} href={att.url || att} target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-1 bg-purple-800 rounded text-sm">
                                    <Download className="w-4 h-4" />
                                    {att.name || `File ${idx + 1}`}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-slate-800 rounded-lg p-8 text-center">
                          <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                          <p className="text-slate-500">No response submitted</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Scoring Section */}
                  <div className="bg-slate-800 rounded-xl p-6">
                    <h3 className="text-sm font-bold text-cyan-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                      <Award className="w-4 h-4" />
                      Your Score (0-5)
                    </h3>

                    <div className="grid grid-cols-6 gap-3 mb-6">
                      {SCORING_LEVELS.map((level) => {
                        const isSelected = currentScore?.score === level.score;
                        return (
                          <button
                            key={level.score}
                            onClick={() => handleScoreSelect(level)}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              isSelected
                                ? 'border-cyan-400 bg-cyan-900/50 ring-2 ring-cyan-400/50'
                                : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
                            }`}
                          >
                            <div className="text-center">
                              <div className={`text-3xl font-black mb-1 ${isSelected ? 'text-cyan-400' : 'text-slate-300'}`}>
                                {level.score}
                              </div>
                              <p className="text-xs font-bold">{level.label}</p>
                              <p className="text-xs text-slate-500">{level.points} pts</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Remarks */}
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-amber-400" />
                        <h4 className="font-bold text-amber-400">Your Remarks</h4>
                        <span className="text-xs bg-red-600 px-2 py-0.5 rounded">Required</span>
                      </div>
                      <textarea
                        value={currentScore?.evaluator_remarks || ''}
                        onChange={(e) => handleRemarksChange(e.target.value)}
                        placeholder="Explain your score..."
                        className="w-full h-24 p-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => currentQuestionIndex > 0 && setCurrentQuestionIndex(currentQuestionIndex - 1)}
                      disabled={currentQuestionIndex === 0}
                      className="px-5 py-2 bg-slate-700 rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleSaveScore(true)}
                        disabled={saving || currentScore?.score === undefined}
                        className="px-5 py-2 bg-slate-600 rounded-lg hover:bg-slate-500 disabled:opacity-50 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Save & Exit
                      </button>
                      <button
                        onClick={() => handleSaveScore(false)}
                        disabled={saving || currentScore?.score === undefined}
                        className="px-6 py-2 bg-cyan-600 rounded-lg font-bold hover:bg-cyan-500 disabled:opacity-50 flex items-center gap-2"
                      >
                        {saving ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Save className="w-4 h-4" />}
                        Save & Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Exit Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4">Unsaved Changes</h3>
            <p className="text-slate-400 mb-6">Exit without saving?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowExitModal(false)} className="px-4 py-2 bg-slate-700 rounded-lg hover:bg-slate-600">
                Cancel
              </button>
              <button onClick={() => navigate('/evaluator/dashboard')} className="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-500">
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluatorAssessmentPage;
